import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import { Plus, Trash2, CheckCircle2, XCircle, Paperclip, X, LayoutDashboard, BarChart3, Users, Wallet, FileText, Landmark, ClipboardCheck, BookOpen, Banknote, AlertCircle, Percent, Scale, BookMarked, TrendingUp, Building2 } from "lucide-react";
import { ColorTab } from "@/components/ui/color-tab";
import BalanceSheetPanel from "@/pages/BalanceSheetPanel";
import PnLStatementPanel from "@/pages/PnLStatementPanel";
import ChartOfAccountsPanel from "@/pages/ChartOfAccountsPanel";
import { toast } from "sonner";
import { FinanceDashboardPanel, VouchersPanel, BankAccountsPanel, ApprovalsPanel, StatusBadge, DayBookPanel, AttachmentInput, AttachmentPreview } from "./FinancePanels";
import { CollectionsDashboardPanel, OutstandingPanel, CustomerStatementPanel } from "./ReceivablesPanels";
import { GstTaxSummaryPanel, SalesRegisterPanel, PurchaseRegisterPanel, HsnSummaryPanel } from "./GstPanels";
import VendorLedgerPanel from "./VendorLedgerPanel";

const CATEGORIES = ["operations", "salary", "rent", "utilities", "logistics", "marketing", "misc"];
const emptyExp = { category: "operations", description: "", amount: 0, date: "", reference: "", paid_to: "", attachment_url: null, attachment_name: null, attachment_mime: null };

export default function AccountsPage() {
  const { user } = useAuth();
  const role = user?.role_id || user?.role;
  const isChief = ["super_admin", "admin", "chief_accountant"].includes(role);
  const [tab, setTab] = useState("dashboard");
  const [pnl, setPnl] = useState(null);
  const [gst, setGst] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [selCustomer, setSelCustomer] = useState("");
  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState(emptyExp);
  const [gstMonth, setGstMonth] = useState("");
  const [gstYear, setGstYear] = useState(new Date().getFullYear());
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectExp, setRejectExp] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadAll = async () => {
    const [p, g, e, c] = await Promise.all([
      api.get("/accounts/pnl"),
      api.get("/accounts/gst-report"),
      api.get("/expenses"),
      api.get("/customers"),
    ]);
    setPnl(p.data); setGst(g.data); setExpenses(e.data); setCustomers(c.data);
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!selCustomer) return setLedger(null);
    api.get(`/accounts/customer-ledger/${selCustomer}`).then((r) => setLedger(r.data));
  }, [selCustomer]);

  const filterGst = async () => {
    const params = {};
    if (gstYear) params.year = gstYear;
    if (gstMonth) params.month = gstMonth;
    const { data } = await api.get("/accounts/gst-report", { params });
    setGst(data);
  };

  const addExpense = async () => {
    if (!expForm.description || !expForm.amount) return toast.error("Description & amount required");
    try {
      await api.post("/expenses", { ...expForm, amount: Number(expForm.amount) });
      toast.success("Expense recorded");
      setExpOpen(false); setExpForm(emptyExp);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeExpense = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    await api.delete(`/expenses/${id}`);
    loadAll();
  };

  const approveExpense = async (e) => {
    try { await api.post(`/expenses/${e.id}/approve`); toast.success("Expense approved"); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const doRejectExpense = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/expenses/${rejectExp.id}/reject`, { reason: rejectReason });
      toast.success("Rejected"); setRejectExp(null); setRejectReason(""); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div>
        <div className="uc-label">Finance</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Accounts</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent p-0 gap-0 flex-wrap h-auto justify-start">
          <ColorTab value="dashboard" tone="blue" icon={LayoutDashboard} testid="acc-tab-dashboard">Dashboard</ColorTab>
          <ColorTab value="pnl" tone="blue" icon={BarChart3} testid="acc-tab-pnl">P&amp;L</ColorTab>
          <ColorTab value="ledger" tone="blue" icon={Users} testid="acc-tab-ledger">Customer Ledger</ColorTab>
          <ColorTab value="expenses" tone="amber" icon={Wallet} testid="acc-tab-expenses">Expenses</ColorTab>
          <ColorTab value="vouchers" tone="purple" icon={FileText} testid="acc-tab-vouchers">Vouchers</ColorTab>
          <ColorTab value="banks" tone="slate" icon={Landmark} testid="acc-tab-banks">Bank Accounts</ColorTab>
          <ColorTab value="approvals" tone="green" icon={ClipboardCheck} testid="acc-tab-approvals" badge={pendingCount}>Approvals</ColorTab>
          <ColorTab value="daybook" tone="orange" icon={BookOpen} testid="acc-tab-daybook">Day Book</ColorTab>
          <ColorTab value="collections" tone="green" icon={Banknote} testid="acc-tab-collections">Collections</ColorTab>
          <ColorTab value="outstanding" tone="red" icon={AlertCircle} testid="acc-tab-outstanding">Outstanding</ColorTab>
          <ColorTab value="vendor-ledger" tone="orange" icon={Building2} testid="acc-tab-vendor-ledger">Vendor Ledger</ColorTab>
          <ColorTab value="gst" tone="purple" icon={Percent} testid="acc-tab-gst">GST Report</ColorTab>
          <ColorTab value="journal" tone="slate" icon={Scale} testid="acc-tab-journal">Journal &amp; Trial Balance</ColorTab>
          <ColorTab value="coa" tone="amber" icon={BookMarked} testid="acc-tab-coa">Chart of Accounts</ColorTab>
          {["super_admin", "chief_accountant", "admin"].includes(user?.role) && (
            <ColorTab value="pnl-statement" tone="green" icon={TrendingUp} testid="acc-tab-pnl-statement">P&amp;L Statement</ColorTab>
          )}
          {["super_admin", "chief_accountant", "admin"].includes(user?.role) && (
            <ColorTab value="balance-sheet" tone="orange" icon={Scale} testid="acc-tab-balance-sheet">Balance Sheet</ColorTab>
          )}
        </TabsList>

        <TabsContent value="dashboard" className="pt-4">
          <FinanceDashboardPanel />
        </TabsContent>

        <TabsContent value="vouchers" className="pt-4">
          <VouchersPanel isChief={isChief} />
        </TabsContent>

        <TabsContent value="banks" className="pt-4">
          <BankAccountsPanel />
        </TabsContent>

        <TabsContent value="approvals" className="pt-4">
          <ApprovalsPanel isChief={isChief} onChanged={setPendingCount} />
        </TabsContent>

        <TabsContent value="daybook" className="pt-4">
          <DayBookPanel />
        </TabsContent>

        <TabsContent value="collections" className="pt-4">
          <CollectionsDashboardPanel />
        </TabsContent>

        <TabsContent value="outstanding" className="pt-4">
          <OutstandingPanel />
        </TabsContent>
        <TabsContent value="vendor-ledger" className="pt-4">
          <VendorLedgerPanel />
        </TabsContent>

        <TabsContent value="pnl" className="pt-4">
          {pnl && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Stat label="Revenue (taxable)" value={inr(pnl.revenue)} accent="text-emerald-700" />
              <Stat label="Cost of Purchases" value={inr(pnl.cost_purchases)} />
              <Stat label="Other Expenses" value={inr(pnl.cost_expenses)} />
              <Stat label="Total Cost" value={inr(pnl.total_cost)} accent="text-red-700" />
              <Stat label="Gross Profit" value={inr(pnl.gross_profit)} accent={pnl.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"} />
              <Stat label="Margin %" value={`${pnl.margin_pct}%`} accent="text-[#C05621]" />
            </div>
          )}
        </TabsContent>

        <TabsContent value="ledger" className="pt-4">
          <CustomerStatementPanel />
        </TabsContent>

        <TabsContent value="expenses" className="pt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={expOpen} onOpenChange={setExpOpen}>
              <DialogTrigger asChild><Button data-testid="expense-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white"><Plus className="w-4 h-4 mr-1" /> Add Expense</Button></DialogTrigger>
              <DialogContent className="max-w-md bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">New Expense</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Category</Label>
                    <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Description *</Label><Input data-testid="expense-desc-input" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} /></div>
                  <div><Label>Amount (₹) *</Label><Input type="number" data-testid="expense-amount-input" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} /></div>
                  <div><Label>Paid to</Label><Input value={expForm.paid_to} onChange={(e) => setExpForm({ ...expForm, paid_to: e.target.value })} /></div>
                  <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} /></div>
                  <div><Label>Reference / Bill No.</Label><Input value={expForm.reference} onChange={(e) => setExpForm({ ...expForm, reference: e.target.value })} /></div>
                  <AttachmentInput
                    label="Bill photo / PDF"
                    hint="Photo of the paper bill or a PDF (max 6 MB). Attaches to this expense for the Chief Accountant to verify."
                    value={{
                      url: expForm.attachment_url,
                      name: expForm.attachment_name,
                      mime: expForm.attachment_mime,
                    }}
                    onChange={(a) => setExpForm({ ...expForm, attachment_url: a?.url || null, attachment_name: a?.name || null, attachment_mime: a?.mime || null })}
                    testId="expense-attach"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setExpOpen(false)}>Cancel</Button>
                  <Button onClick={addExpense} data-testid="expense-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left uc-label border-b border-[#EAE5D9]"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Paid to</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Actions</th></tr></thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-2 text-[#8B7355]">{fmtDate(e.date)}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{e.category}</Badge></td>
                    <td className="px-4 py-2">{e.description}</td>
                    <td className="px-4 py-2 text-[#5C4033]">{e.paid_to || "—"}</td>
                    <td className="px-4 py-2"><StatusBadge status={e.status || "approved"} /></td>
                    <td className="px-4 py-2 text-right font-semibold text-red-700">
                      {inr(e.amount)}
                      {e.attachment_url && <AttachmentPreview url={e.attachment_url} name={e.attachment_name} mime={e.attachment_mime} className="inline-block ml-2 align-middle" />}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        {e.status === "pending" && isChief && (
                          <>
                            <button onClick={() => approveExpense(e)} data-testid={`expense-approve-${e.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><CheckCircle2 className="w-4 h-4" /></button>
                            <button onClick={() => { setRejectExp(e); setRejectReason(""); }} data-testid={`expense-reject-${e.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><XCircle className="w-4 h-4" /></button>
                          </>
                        )}
                        {e.status === "pending" && !isChief && (
                          <span className="text-xs text-[#8B7355] italic">Awaiting Chief</span>
                        )}
                        {isChief && <button onClick={() => removeExpense(e.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No expenses yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gst" className="pt-4 space-y-4">
          <Tabs defaultValue="tax-summary" className="space-y-4">
            <TabsList className="bg-transparent p-0 gap-0 flex-wrap h-auto justify-start">
              <ColorTab value="tax-summary" tone="orange" icon={Percent} testid="gst-sub-tax-summary">Tax Summary</ColorTab>
              <ColorTab value="sales-register" tone="green" icon={BarChart3} testid="gst-sub-sales">Sales Register</ColorTab>
              <ColorTab value="purchase-register" tone="amber" icon={FileText} testid="gst-sub-purchase">Purchase Register</ColorTab>
              <ColorTab value="hsn" tone="purple" icon={Scale} testid="gst-sub-hsn">HSN Summary</ColorTab>
            </TabsList>
            <TabsContent value="tax-summary"><GstTaxSummaryPanel /></TabsContent>
            <TabsContent value="sales-register"><SalesRegisterPanel /></TabsContent>
            <TabsContent value="purchase-register"><PurchaseRegisterPanel /></TabsContent>
            <TabsContent value="hsn"><HsnSummaryPanel /></TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="journal" className="pt-4 space-y-4">
          <JournalPanel />
        </TabsContent>

        <TabsContent value="coa" className="pt-4">
          <ChartOfAccountsPanel />
        </TabsContent>

        {["super_admin", "chief_accountant", "admin"].includes(user?.role) && (
          <TabsContent value="pnl-statement" className="pt-4">
            <PnLStatementPanel />
          </TabsContent>
        )}

        {["super_admin", "chief_accountant", "admin"].includes(user?.role) && (
          <TabsContent value="balance-sheet" className="pt-4">
            <BalanceSheetPanel />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!rejectExp} onOpenChange={(o) => !o && setRejectExp(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Reject Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Rejection reason *</Label>
            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="expense-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectExp(null)}>Cancel</Button>
            <Button onClick={doRejectExpense} data-testid="expense-reject-confirm" className="bg-red-600 hover:bg-red-700 text-white">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JournalPanel() {
  const [tb, setTb] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState({ reference_type: "", account_code: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => v && params.append(k, v));
      const [tbRes, jRes] = await Promise.all([
        api.get(`/accounts/trial-balance?${params.toString()}`),
        api.get(`/accounts/journal?limit=200&${params.toString()}`),
      ]);
      setTb(tbRes.data);
      setEntries(jRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const res = await api.post("/accounts/rebuild");
      toast.success(`Rebuilt · Sales ${res.data.sales} · POs ${res.data.purchase_orders} · Expenses ${res.data.expenses}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const downloadExport = async (fmt) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => v && params.append(k, v));
      const path = fmt === "tally-xml" ? "tally-xml" : "csv";
      const res = await api.get(`/accounts/journal/export/${path}?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: fmt === "tally-xml" ? "application/xml" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "tally-xml"
        ? `tally_vouchers_${filter.date_from || "all"}_${filter.date_to || "now"}.xml`
        : `journal_${filter.date_from || "all"}_${filter.date_to || "now"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt === "tally-xml" ? "Tally XML" : "CSV"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="acc-journal-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} data-testid="journal-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} data-testid="journal-date-to" />
        </div>
        <div>
          <Label className="text-xs">Reference type</Label>
          <Select value={filter.reference_type || "all"} onValueChange={(v) => setFilter({ ...filter, reference_type: v === "all" ? "" : v })}>
            <SelectTrigger className="w-[180px]" data-testid="journal-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pos_sale">POS Sale</SelectItem>
              <SelectItem value="pos_void">POS Void</SelectItem>
              <SelectItem value="po_receive">PO Receive</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} className="bg-[#2C1810] text-white" data-testid="journal-apply">Apply</Button>
        <Button variant="outline" onClick={rebuild} disabled={rebuilding} data-testid="journal-rebuild">
          {rebuilding ? "Rebuilding…" : "Rebuild from history"}
        </Button>
        <Button variant="outline" onClick={() => downloadExport("csv")} data-testid="journal-export-csv">
          Export CSV
        </Button>
        <Button variant="outline" onClick={() => downloadExport("tally-xml")} data-testid="journal-export-tally">
          Export Tally XML
        </Button>
      </div>

      {tb && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-[Outfit] text-lg font-semibold">Trial Balance</h3>
            <Badge className={tb.balanced ? "bg-emerald-600 text-white" : "bg-red-600 text-white"} data-testid="tb-balanced">
              {tb.balanced ? "Balanced" : "OUT OF BALANCE"}
            </Badge>
            <span className="text-xs text-[#8B7355]">DR ₹{tb.total_debit.toLocaleString()} · CR ₹{tb.total_credit.toLocaleString()}</span>
          </div>
          <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Account</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Debit</th>
                  <th className="text-right px-4 py-2">Credit</th>
                  <th className="text-right px-4 py-2">Balance</th>
                </tr>
              </thead>
              <tbody data-testid="tb-rows">
                {tb.accounts.map((a) => (
                  <tr key={a.code} className="border-b border-[#EAE5D9] last:border-0" data-testid={`tb-row-${a.code}`}>
                    <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                    <td className="px-4 py-2 font-medium">{a.name}</td>
                    <td className="px-4 py-2 text-xs uppercase text-[#8B7355]">{a.type}</td>
                    <td className="px-4 py-2 text-right">{a.debit ? inr(a.debit) : "—"}</td>
                    <td className="px-4 py-2 text-right">{a.credit ? inr(a.credit) : "—"}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${a.balance_side === "debit" ? "text-emerald-700" : "text-[#C05621]"}`}>
                      {inr(a.balance)} <span className="text-[10px] uppercase font-normal">{a.balance_side}</span>
                    </td>
                  </tr>
                ))}
                {tb.accounts.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-[#8B7355]">No entries in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-[Outfit] text-lg font-semibold mb-2">Journal Entries · latest {entries.length}</h3>
        <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Ref</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="journal-rows">
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FAF6EE]" data-testid={`journal-row-${e.id}`}>
                  <td className="px-4 py-2 text-xs text-[#8B7355]">{fmtDate(e.date)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.reference_number || e.reference_id?.slice(0, 8)}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className={`text-[10px] ${e.reversed ? "opacity-50 line-through" : ""}`}>
                      {e.reference_type}{e.reversal_of ? " (reversal)" : ""}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs">{e.description}</td>
                  <td className="px-4 py-2 text-right font-semibold">{inr(e.total_debit)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setSelected(e)} className="text-xs text-[#C05621] hover:underline" data-testid={`journal-open-${e.id}`}>view</button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8B7355]">No entries — try “Rebuild from history” to backfill.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">
              Journal Entry — {selected?.reference_number || selected?.reference_id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-[#8B7355]">Date:</span> {fmtDate(selected.date)}</div>
                <div><span className="text-[#8B7355]">Type:</span> {selected.reference_type}</div>
                <div className="col-span-2"><span className="text-[#8B7355]">Description:</span> {selected.description}</div>
              </div>
              <table className="w-full text-sm border border-[#EAE5D9] rounded">
                <thead className="bg-[#FAF6EE] text-[#8B7355] text-[10px] uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Account</th>
                    <th className="text-right px-3 py-2">Debit</th>
                    <th className="text-right px-3 py-2">Credit</th>
                    <th className="text-left px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map((l, i) => (
                    <tr key={i} className="border-t border-[#EAE5D9]">
                      <td className="px-3 py-2"><span className="font-mono text-xs mr-2">{l.account_code}</span>{l.account_name}{l.party ? <span className="text-[#8B7355] text-xs ml-1">· {l.party}</span> : null}</td>
                      <td className="px-3 py-2 text-right">{l.debit ? inr(l.debit) : "—"}</td>
                      <td className="px-3 py-2 text-right">{l.credit ? inr(l.credit) : "—"}</td>
                      <td className="px-3 py-2 text-xs text-[#8B7355]">{l.notes || ""}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#2C1810] font-semibold">
                    <td className="px-3 py-2">TOTALS</td>
                    <td className="px-3 py-2 text-right">{inr(selected.total_debit)}</td>
                    <td className="px-3 py-2 text-right">{inr(selected.total_credit)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-lg p-5">
    <div className="uc-label">{label}</div>
    <div className={`font-[Outfit] text-2xl font-semibold mt-1 tracking-tight ${accent || ""}`}>{value}</div>
  </div>
);
